import { authFailure, isStepUpDeclined } from "../../lib/http/authErrors";
import { PrescriptionDraftEnvelopeError } from "../../api/healthcare/PrescriptionDraftApi";

export type PrescriptionDraftFailure =
	| { kind: "validation"; fields: Record<string, string>; message: string }
	| { kind: "stale_revision"; currentRevision?: number; message: string }
	| { kind: "request_id_reused"; message: string }
	| { kind: "conflict"; reason?: string; message: string }
	| { kind: "forbidden"; reason?: string; message: string }
	| { kind: "not_found"; reason?: string; message: string }
	| { kind: "unavailable"; message: string }
	| { kind: "stepup_declined"; message: string }
	| { kind: "ambiguous"; message: string };

const REASON_MESSAGES: Record<string, string> = {
	caller_not_direct_staff_user: "Prescription drafts can only be worked on by a signed-in staff prescriber.",
	practice_capability_not_approved: "This practice is not approved for prescribing, so prescription drafts are unavailable.",
	permission_missing: "Your account is not a designated prescriber for this practice.",
	prescriber_identity_missing: "Your prescriber identity is not recorded in Healthcare. Ask your practice administrator to set it up before drafting.",
	no_current_affiliation: "You have no current affiliation with the selected organisation, so this draft cannot be saved.",
	prescriber_identity_changed: "Your prescriber identity no longer matches this draft, so it cannot be edited.",
	organisation_not_selectable: "That organisation cannot be selected for this practice.",
	patient_not_found: "This patient record is not available for prescribing.",
	patient_merged_away: "This patient record was merged into another record. Open the surviving record to work on prescription drafts.",
	prescription_not_found: "This draft was not found. It may belong to another prescriber or patient.",
	not_a_draft: "This record is no longer a draft and cannot be edited here.",
};

const AMBIGUOUS = "We could not confirm whether your save reached Healthcare. Retrying is safe: it will not create a duplicate draft or overwrite a newer one.";

const reasonMessage = (reason: string | undefined, fallback: string): string => (reason && REASON_MESSAGES[reason]) || fallback;

// CU-14ygtkjeh2z: Laravel's own 422 body is {message, errors: {field: [..]}}; the §11 envelope carries details.reason/field instead.
const laravelFieldErrors = (data: unknown): Record<string, string> | null => {
	const errors = (data as { errors?: unknown } | undefined)?.errors;
	if (!errors || typeof errors !== "object") return null;
	return Object.fromEntries(
		Object.entries(errors as Record<string, unknown>)
			.map(([field, messages]) => [field, Array.isArray(messages) && typeof messages[0] === "string" ? messages[0] : "This value is not accepted."])
	);
};

export const classifyPrescriptionDraftFailure = (error: unknown): PrescriptionDraftFailure => {
	if (isStepUpDeclined(error)) return { kind: "stepup_declined", message: "" };
	if (error instanceof PrescriptionDraftEnvelopeError) return { kind: "unavailable", message: "Healthcare returned an unexpected response. Nothing is shown rather than risk showing the wrong draft." };

	const failure = authFailure(error);
	const reason = typeof failure.details.reason === "string" ? failure.details.reason : undefined;
	const data = (error as { response?: { data?: unknown } })?.response?.data;

	if (failure.status === undefined || failure.status >= 500) return { kind: "ambiguous", message: AMBIGUOUS };

	if (failure.status === 422) {
		const fields = laravelFieldErrors(data);
		if (fields) return { kind: "validation", fields, message: "Some details need correcting before this draft can be saved." };
		const field = typeof failure.details.field === "string" ? failure.details.field : undefined;
		const message = reasonMessage(reason, "Some details need correcting before this draft can be saved.");
		return { kind: "validation", fields: field ? { [field]: message } : {}, message };
	}

	if (failure.status === 409) {
		if (reason === "stale_revision") {
			const current = Number(failure.details.current_revision);
			return {
				kind: "stale_revision",
				currentRevision: Number.isInteger(current) ? current : undefined,
				message: "This draft was changed since you opened it. Load the latest version before saving again.",
			};
		}
		if (reason === "client_request_id_reused") {
			return { kind: "request_id_reused", message: "Your earlier attempt was already saved with different details. Review the saved draft before making further changes." };
		}
		return { kind: "conflict", reason, message: reasonMessage(reason, "This draft cannot be saved in its current state.") };
	}

	if (failure.status === 403) {
		const key = failure.code === "permission_denied" ? "permission_missing" : reason;
		return { kind: "forbidden", reason: key, message: reasonMessage(key, "You are not allowed to work on prescription drafts here.") };
	}

	if (failure.status === 404) {
		// CU-14ygtkjeh2z: a 404 without the §11 envelope is the router itself: the draft API is not active in this environment.
		if (!failure.code) return { kind: "unavailable", message: "Prescription drafting is not yet available in this environment." };
		return { kind: "not_found", reason, message: reasonMessage(reason, "This prescription draft was not found.") };
	}

	if (failure.status === 401) return { kind: "unavailable", message: "Your session has ended. Sign in again to continue." };

	return { kind: "unavailable", message: failure.message ?? "Prescription drafts are unavailable right now." };
};
